import { supabase } from './supabaseClient';
import { obtenerNegocio } from './negociosService';

// Comprobante de venta (no fiscal, sin CAI). Número correlativo por negocio;
// volver a generarlo para la misma venta devuelve el mismo número (sql/093).

export const numeroComprobante = (n) => `CV-${String(n).padStart(6, '0')}`;

// Al cliente se le muestra la forma de pago general, no el nombre interno de
// la cuenta (ej. "BAC LOLITAS" → "Transferencia / depósito").
const FORMA_PAGO = {
  efectivo: 'Efectivo',
  banco: 'Transferencia / depósito',
  pos: 'Tarjeta',
  tarjeta_credito: 'Tarjeta',
  otro: 'Otro',
};

export async function emitirComprobante(ticket, { clienteNombre, clienteRtn } = {}) {
  const { data, error } = await supabase.rpc('emitir_comprobante', {
    p_ticket: ticket,
    p_cliente_nombre: clienteNombre || null,
    p_cliente_rtn: clienteRtn || null,
  });
  if (error) throw error;
  return data;
}

// Comprobante ya emitido de una venta (o null).
export async function comprobanteDeVenta(ticket) {
  const { data } = await supabase.from('comprobantes').select('*')
    .eq('ticket_id', ticket).eq('tipo', 'comprobante').maybeSingle();
  return data || null;
}

// Guarda el RTN en la ficha del cliente para la próxima vez.
export async function guardarRtnCliente(clienteId, rtn) {
  if (!clienteId) return;
  await supabase.from('clientes').update({ rtn: rtn?.trim() || null }).eq('id', clienteId);
}

// Todo lo necesario para armar el comprobante de una venta (ticket).
export async function datosDeVenta(ticket) {
  const [rv, rc, re, negocio] = await Promise.all([
    supabase.from('ventas')
      .select('nombre_producto, codigo_producto, cantidad, precio_unitario, total, metodo_pago, vendido_en, a_credito, cliente_id, clientes ( nombre, rtn, telefono ), cuentas ( tipo )')
      .eq('ticket_id', ticket),
    supabase.from('cuentas_por_cobrar').select('monto_total, monto_pagado').eq('ticket_id', ticket).maybeSingle(),
    supabase.from('ingresos_libres').select('monto').eq('ticket_venta', ticket),
    obtenerNegocio(),
  ]);
  if (rv.error) throw rv.error;
  const filas = rv.data || [];
  if (!filas.length) throw Object.assign(new Error('No se encontró esa venta.'), { code: 'DATO_INVALIDO' });

  const r2 = (n) => Math.round(Number(n || 0) * 100) / 100;
  const items = filas.map((f) => ({
    descripcion: f.nombre_producto || 'Producto',
    codigo: f.codigo_producto || '',
    cantidad: Number(f.cantidad),
    precio_unitario: Number(f.precio_unitario),
    total: Number(f.total),
  }));
  const bruto = r2(items.reduce((s, i) => s + i.precio_unitario * i.cantidad, 0));
  const neto = r2(items.reduce((s, i) => s + i.total, 0));
  const credito = filas.some((f) => f.a_credito);
  const cxc = rc.data || null;
  const envio = credito
    ? r2(Math.max(Number(cxc?.monto_total || neto) - neto, 0))
    : r2((re.data || []).reduce((s, x) => s + Number(x.monto || 0), 0));
  const total = r2(neto + envio);
  const pagado = credito ? r2(cxc?.monto_pagado) : total;
  const cliente = filas[0].clientes || null;

  return {
    negocio,
    venta: {
      ticket,
      fecha: filas[0].vendido_en,
      metodo: credito ? 'Crédito' : (FORMA_PAGO[filas[0].cuentas?.tipo] || ''),
      credito,
      clienteId: filas[0].cliente_id || null,
      cliente,
      items,
      subtotal: bruto,
      descuento: r2(bruto - neto),
      envio,
      total,
      pagado,
      saldo: credito ? r2(total - pagado) : 0,
    },
  };
}
